import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { ADMIN_NAV, navPath } from './adminNav';

// Rail state is persisted so an admin who works collapsed stays collapsed.
const COLLAPSED_KEY = 'admin.nav.collapsed';
const EXPANDED_KEY = 'admin.nav.expandedGroups';

export function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function readExpandedGroups() {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function AdminNavRail({ collapsed, setCollapsed }) {
  const { groupId } = useParams();
  const [expanded, setExpanded] = React.useState(
    () => readExpandedGroups() || ADMIN_NAV.map((g) => g.id)
  );

  // The group you navigate into always opens, even if it was collapsed.
  React.useEffect(() => {
    if (groupId) setExpanded((prev) => (prev.includes(groupId) ? prev : [...prev, groupId]));
  }, [groupId]);

  React.useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify(expanded));
    } catch { /* storage unavailable, fall back to in-memory state */ }
  }, [expanded]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, String(next));
    } catch { /* storage unavailable */ }
  };

  const toggleGroup = (id) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  const itemClass = ({ isActive }) =>
    `flex items-center ${collapsed ? 'justify-center px-2' : 'gap-2.5 px-3'} py-2 rounded-lg text-sm font-rasa transition-colors ${
      isActive
        ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 font-medium'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50'
    }`;

  return (
    <nav
      aria-label="Admin sections"
      className={`hidden md:flex flex-col flex-shrink-0 ${collapsed ? 'w-14' : 'w-56'} border-r border-gray-200 dark:border-gray-700 overflow-y-auto transition-all duration-200`}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        className={`flex items-center ${collapsed ? 'justify-center' : 'justify-end'} px-3 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200`}
      >
        {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
      </button>

      <div className="px-2 pb-4 space-y-1">
        {ADMIN_NAV.map((group) => {
          const isOpen = expanded.includes(group.id);
          const GroupIcon = group.icon;

          // Collapsed rail has no room for headers, so show icons only with a
          // separator between groups.
          if (collapsed) {
            return (
              <div
                key={group.id}
                className="pt-2 mt-2 first:mt-0 first:pt-0 border-t first:border-t-0 border-gray-200 dark:border-gray-700 space-y-1"
              >
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.id}
                      to={navPath(group.id, item.id)}
                      className={itemClass}
                      title={`${group.label}: ${item.label}`}
                    >
                      <Icon size={16} />
                    </NavLink>
                  );
                })}
              </div>
            );
          }

          return (
            <div key={group.id}>
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-rasa font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <GroupIcon size={14} />
                <span className="truncate">{group.label}</span>
              </button>
              {isOpen && (
                <div className="space-y-0.5 pl-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink key={item.id} to={navPath(group.id, item.id)} className={itemClass}>
                        <Icon size={16} />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
